import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconButton } from '../components/ui';

// Bump this whenever the receivables help content is meaningfully updated.
const LAST_REVISED = 'October 21, 2026';

const sections = [
  { id: 'overview', title: '1. Overview & Access' },
  { id: 'finding', title: '2. Finding Invoices (Web)' },
  { id: 'aging', title: '3. Aging Columns & "61+ only"' },
  { id: 'viewing', title: '4. Viewing an Invoice & Its History' },
  { id: 'status', title: '5. Posting a Status' },
  { id: 'revision', title: '6. Posting a Revision' },
  { id: 'receipts', title: '7. Adding Receipts' },
  { id: 'toolbar', title: '8. Posting from the Outlook Toolbar' },
  { id: 'effective', title: '9. Effective Dates & Debug Mode' },
];

function Step({ number, children }) {
  return (
    <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '50%', background: 'var(--primary)',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '14px', fontWeight: 700, flexShrink: 0, marginTop: '2px',
      }}>{number}</div>
      <div style={{ flex: 1, fontSize: '14px', lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function Tip({ children }) {
  return (
    <div style={{
      background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px',
      padding: '10px 14px', fontSize: '13px', lineHeight: 1.5, margin: '12px 0',
      color: '#1e40af',
    }}>
      <strong>Tip:</strong> {children}
    </div>
  );
}

function Note({ children }) {
  return (
    <div style={{
      background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '6px',
      padding: '10px 14px', fontSize: '13px', lineHeight: 1.5, margin: '12px 0',
      color: '#92400e',
    }}>
      <strong>Note:</strong> {children}
    </div>
  );
}

function UILabel({ children }) {
  return (
    <span style={{
      display: 'inline-block', background: 'var(--primary-light)', borderRadius: '4px',
      padding: '1px 6px', fontSize: '13px', fontWeight: 600, color: 'var(--primary)',
    }}>{children}</span>
  );
}

function SectionCard({ id, title, children, isOpen, onToggle }) {
  return (
    <div id={id} style={{
      background: '#fff', borderRadius: 'var(--radius)', border: '1px solid var(--gray-200)',
      marginBottom: '12px', overflow: 'hidden',
    }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', padding: '16px 20px', background: 'none', border: 'none',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: '16px', fontWeight: 700, color: 'var(--gray-800)', textAlign: 'left',
        }}
      >
        <span>{title}</span>
        <span style={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform 0.2s', fontSize: '12px', color: 'var(--gray-400)',
        }}>&#x25BC;</span>
      </button>
      {isOpen && (
        <div style={{ padding: '0 20px 20px', borderTop: '1px solid var(--gray-100)' }}>
          {children}
        </div>
      )}
    </div>
  );
}

const para = { fontSize: '14px', lineHeight: 1.6, color: 'var(--gray-600)' };
const h4 = { fontSize: '15px', marginTop: '20px', marginBottom: '8px' };
const list = { fontSize: '14px', lineHeight: 1.8, color: 'var(--gray-600)', paddingLeft: '20px' };

export default function InvoicingHelpPage() {
  const location = useLocation();
  const requestedSection = new URLSearchParams(location.search).get('section');
  const validSection = requestedSection && sections.some((s) => s.id === requestedSection)
    ? requestedSection
    : null;

  const [openSections, setOpenSections] = useState(() => new Set([validSection || 'overview']));

  useEffect(() => {
    if (!validSection) return;
    setOpenSections((prev) => {
      const next = new Set(prev);
      next.add(validSection);
      return next;
    });
    const el = document.getElementById(validSection);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, [validSection]);

  const toggle = (id) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenSections(new Set(sections.map((s) => s.id)));
  const collapseAll = () => setOpenSections(new Set());

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px 16px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--gray-800)', marginBottom: '4px' }}>
          Receivables Help
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--gray-500)', margin: '0 0 4px' }}>
          How to review, view, and post to outstanding invoices &mdash; on the <UILabel>Receivables</UILabel> screen, a job page's <UILabel>Receivables</UILabel> tab, and the Outlook toolbar. Creating and approving invoices is covered in the main <Link to="/help?section=jobs-invoicing" style={{ color: 'var(--primary)' }}>Help</Link>.
        </p>
        <p style={{ fontSize: '12px', color: 'var(--gray-400)', margin: '0 0 12px' }}>
          Last revised: {LAST_REVISED}
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <IconButton icon="expandAll" title="Expand all" onClick={expandAll} />
          <IconButton icon="collapseAll" title="Collapse all" onClick={collapseAll} />
        </div>
      </div>

      {/* Table of Contents */}
      <div style={{
        background: 'var(--gray-50)', borderRadius: 'var(--radius)', padding: '14px 18px',
        marginBottom: '16px', border: '1px solid var(--gray-200)',
      }}>
        <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '8px', color: 'var(--gray-700)' }}>
          Contents
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => { e.preventDefault(); toggle(s.id); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' }); }}
              style={{ fontSize: '13px', color: 'var(--primary)', textDecoration: 'none', padding: '2px 0' }}
            >
              {s.title}
            </a>
          ))}
        </div>
      </div>

      {/* 1. Overview & Access */}
      <SectionCard id="overview" title="1. Overview & Access" isOpen={openSections.has('overview')} onToggle={() => toggle('overview')}>
        <p style={{ ...para, marginTop: '12px' }}>
          Invoice work happens in two places: the <UILabel>Receivables</UILabel> screen on the website (and the <UILabel>Receivables</UILabel> tab of a job's page), and the RCPTime add-in on the Outlook toolbar. Both read outstanding posted invoices from Navision and record activity in the invoice's Status Log.
        </p>
        <h4 style={h4}>Who can see Receivables</h4>
        <ul style={list}>
          <li>Any user with <strong>Navision Web Access</strong> sees <UILabel>Receivables</UILabel> in the top navigation bar (<UILabel>A/R</UILabel> on small screens).</li>
          <li><strong>Accounting Access</strong> and <strong>Executive Access</strong> users see <em>all</em> jobs' outstanding invoices.</li>
          <li>Everyone else sees only invoices on jobs where they are the <strong>Project Manager, Director, or Executive</strong>.</li>
          <li>A specific job opened from an A/R report link (a <UILabel>?job=</UILabel> deep link) is shown to any Navision Web Access user, even without a role on that job.</li>
        </ul>
        <h4 style={h4}>What each group can do</h4>
        <ul style={list}>
          <li><strong>Any Navision Web Access user</strong> &mdash; view invoices and their documents, see the status-log history, and <UILabel>Post Status</UILabel>.</li>
          <li><strong>Executive Access</strong> &mdash; the same, but across <em>all</em> jobs. View only: no revision or receipts buttons, and no invoice posting from the Outlook toolbar.</li>
          <li><strong>Accounting Access</strong> &mdash; everything above, plus <UILabel>Post Revision</UILabel>, <UILabel>Add Receipts</UILabel>, and posting invoice links from the Outlook toolbar.</li>
        </ul>
        <Note>
          Access reflects your Navision resource permissions. If access was granted recently and you don't see everything you expect, re-open Receivables (permissions refresh once per session) or use <UILabel>Refresh My Permissions</UILabel> on <UILabel>Settings</UILabel> &rarr; <UILabel>Admin</UILabel>.
        </Note>
      </SectionCard>

      {/* 2. Finding Invoices */}
      <SectionCard id="finding" title="2. Finding Invoices (Web)" isOpen={openSections.has('finding')} onToggle={() => toggle('finding')}>
        <Step number="1">
          From the top navigation bar, open <UILabel>Receivables</UILabel> (<UILabel>A/R</UILabel> on small screens).
        </Step>
        <Step number="2">
          Invoices are grouped <strong>parent customer &rarr; job &rarr; invoice</strong>. Everything starts collapsed. Click a customer row to expand its jobs, and a job row to expand its invoices.
        </Step>
        <Step number="3">
          Use the <UILabel>&#8862;</UILabel> (expand all) / <UILabel>&#8863;</UILabel> (collapse all) icons (top right) to open or close everything at once. Expand all opens all customers and jobs (but not the invoices' status logs); collapse all closes everything.
        </Step>
        <Step number="4">
          Each invoice row shows, left to right: the invoice number, the outstanding amount, the posting date, and the action buttons. Invoice numbers display with a leading <UILabel>#</UILabel> (for example, <UILabel>#123456</UILabel>).
        </Step>
        <Note>
          The leading <UILabel>#</UILabel> can be turned off for an individual user. In <UILabel>Manage Users</UILabel> (<UILabel>Settings</UILabel> &rarr; <UILabel>Admin</UILabel>), check <UILabel>Hide #</UILabel> for that user and their invoice numbers show without the prefix.
        </Note>
        <Step number="5">
          Click the refresh icon (<UILabel>&#8635;</UILabel>) at any time to reload the list and pull in status or revisions posted elsewhere (for example, from the Outlook toolbar).
        </Step>
        <h4 style={h4}>Opening from an A/R report link</h4>
        <p style={para}>
          A report link can open Receivables filtered to a single <strong>job</strong> (<UILabel>Receivables &mdash; Job XYZ</UILabel>) or to a whole <strong>customer</strong> (<UILabel>Receivables &mdash; Customer XYZ</UILabel>). A customer filter shows every job under that customer, including its child customers.
        </p>
        <Step number="6">
          To step back to your full list, click <UILabel>Remove Filter</UILabel> next to that heading. You then see every customer and job you would normally see visiting the page directly.
        </Step>
      </SectionCard>

      {/* 3. Aging */}
      <SectionCard id="aging" title='3. Aging Columns & "61+ only"' isOpen={openSections.has('aging')} onToggle={() => toggle('aging')}>
        <p style={{ ...para, marginTop: '12px' }}>
          Aging totals mirror the Navision <em>Aged Accts. Receivable by Job</em> report. Each invoice is aged by the number of days since its <strong>posting date</strong>, and its full outstanding amount falls into exactly one aging bucket. Totals are rolled up on each <strong>customer</strong> and <strong>job</strong> row, plus a grand total at the bottom.
        </p>
        <h4 style={h4}>Default columns</h4>
        <ul style={list}>
          <li><strong>Current</strong> &mdash; 0&ndash;30 days.</li>
          <li><strong>31&ndash;60</strong>, <strong>61&ndash;90</strong>, <strong>90+</strong> days.</li>
        </ul>
        <h4 style={h4}>"61+ only"</h4>
        <p style={para}>
          Check <UILabel>61+ only</UILabel> (top right) to focus on genuinely aged receivables. Every invoice 60 days old or newer is dropped entirely &mdash; from the rows and the totals &mdash; and jobs or customers left with nothing over 60 days disappear. The columns switch to <strong>61&ndash;90 / 91&ndash;120 / 121&ndash;150 / 151+</strong>.
        </p>
        <Tip>
          An individual invoice line shows a single amount, not aging columns &mdash; an invoice is only ever one age. The aging breakdown lives on the job, customer, and grand-total rows.
        </Tip>
      </SectionCard>

      {/* 4. Viewing */}
      <SectionCard id="viewing" title="4. Viewing an Invoice & Its History" isOpen={openSections.has('viewing')} onToggle={() => toggle('viewing')}>
        <h4 style={{ ...h4, marginTop: '12px' }}>View the invoice document</h4>
        <Step number="1">
          Click <UILabel>View Inv</UILabel> on the invoice row. The latest document image for that invoice opens in a new browser tab, retrieved directly from the link stored in the invoice's Status Log.
        </Step>
        <h4 style={h4}>Receipts and time detail notes</h4>
        <p style={{ fontSize: '14px', lineHeight: 1.6, color: 'var(--gray-600)' }}>
          Beside the invoice number, <UILabel>&#129534;</UILabel> opens the invoice's receipts as one PDF (shown only when the invoice has receipts), and <UILabel>&#128221;</UILabel> prints the time detail notes: the date, employee, hours, and timesheet note of each time entry on the invoice, by task line.
        </p>
        <h4 style={h4}>See the status-log history</h4>
        <Step number="2">
          Click the triangle (<UILabel>&#9654;</UILabel>) at the start of the invoice row to expand its Status Log. Each entry shows its effective date, the user, and the description, newest first.
        </Step>
        <Step number="3">
          Entries that carry a document or link show an <UILabel>open link</UILabel> action &mdash; click it to open that specific file.
        </Step>
        <Tip>
          After you post a status or revision, that invoice's history refreshes automatically so your new entry appears at the top.
        </Tip>
      </SectionCard>

      {/* 5. Post Status */}
      <SectionCard id="status" title="5. Posting a Status" isOpen={openSections.has('status')} onToggle={() => toggle('status')}>
        <p style={{ ...para, marginTop: '12px' }}>
          A status is a short note recorded against the invoice (for example, a collection note). Any Navision Web Access user can post one.
        </p>
        <Step number="1">
          Click <UILabel>Post Status</UILabel> on the invoice row.
        </Step>
        <Step number="2">
          Type the status text and confirm the <UILabel>Effective date</UILabel> (defaults to today).
        </Step>
        <Step number="3">
          Click <UILabel>Post</UILabel>. The note is written to the invoice's Status Log and the history refreshes.
        </Step>
      </SectionCard>

      {/* 6. Post Revision */}
      <SectionCard id="revision" title="6. Posting a Revision" isOpen={openSections.has('revision')} onToggle={() => toggle('revision')}>
        <p style={{ ...para, marginTop: '12px' }}>
          A revision replaces the invoice's document image with a revised file. <strong>Accounting Access</strong> is required.
        </p>
        <Step number="1">
          Click <UILabel>Post Revision</UILabel> on the invoice row.
        </Step>
        <Step number="2">
          Drag the revised file into the drop area, or click it to select a file. A PDF or an image is accepted; images are converted to a PDF automatically.
        </Step>
        <Step number="3">
          The note is pre-filled as <em>Invoice Revised [date]</em>. Edit it if you like, and confirm the effective date.
        </Step>
        <Step number="4">
          Click <UILabel>Post</UILabel>. The file is saved to the SharePoint Revisions library and logged as a new document-image entry on the invoice.
        </Step>
        <Tip>
          Revisions are filed under <UILabel>Inv/&lt;year&gt;/&lt;month&gt;/</UILabel> (the month of the invoice's first document image) and named <UILabel>JobNo_InvoiceNo_date-time.pdf</UILabel>.
        </Tip>
      </SectionCard>

      {/* 7. Add Receipts */}
      <SectionCard id="receipts" title="7. Adding Receipts" isOpen={openSections.has('receipts')} onToggle={() => toggle('receipts')}>
        <p style={{ ...para, marginTop: '12px' }}>
          <UILabel>Add Receipts</UILabel> generates the invoice's receipt report and appends those receipts to the invoice's current document image as a new revision, then opens the combined PDF. <strong>Accounting Access</strong> is required.
        </p>
        <Step number="1">
          Click <UILabel>Add Receipts</UILabel> on the invoice row.
        </Step>
        <Step number="2">
          The system gathers the invoice's receipts, appends the ones that are not already attached, saves the combined file as a new document image, and opens it in a new browser tab. There is nothing to attach and no effective-date option &mdash; the current date and time are always used.
        </Step>
        <Step number="3">
          A message tells you how many receipts were added.
        </Step>
        <Tip>
          Receipts already attached to the invoice are detected (each carries a hidden code in the document) and are never added twice &mdash; only receipts that were not there before are appended. If every receipt is already attached, nothing is added and the message says so.
        </Tip>
        <Note>
          If the invoice has no document image yet, there is nothing to append to and the action reports that no existing document was found. Post a revision first, or view the invoice to confirm a document exists.
        </Note>
      </SectionCard>

      {/* 8. Outlook toolbar */}
      <SectionCard id="toolbar" title="8. Posting from the Outlook Toolbar" isOpen={openSections.has('toolbar')} onToggle={() => toggle('toolbar')}>
        <p style={{ ...para, marginTop: '12px' }}>
          Accounting Access users can link an email (for example, customer correspondence about a bill) directly to an invoice from the Outlook add-in.
        </p>
        <Step number="1">
          Open the email in Outlook and launch the RCPTime add-in from the toolbar.
        </Step>
        <Step number="2">
          Select a customer <em>or</em> a job. Selecting a customer lists that customer's outstanding invoices &mdash; including its child customers and the invoices for any job that belongs to it, even when an invoice is billed to a related company. Selecting a job lists that job's invoices, even with no customer chosen. Each entry shows the invoice number, posting date, job number, and outstanding balance.
        </Step>
        <Step number="3">
          Pick an invoice from the <UILabel>Select invoice</UILabel> list, or type an invoice number into the manual entry box. Changing or clearing the job or customer clears the selected invoice.
        </Step>
        <Tip>
          For Accounting Access users, inactive jobs (and their customers) that still have an open invoice appear in the selectors automatically &mdash; you do not need <UILabel>Show Inactive</UILabel> to find them. <UILabel>Show Inactive</UILabel> additionally lists every inactive job, customer, and opportunity.
        </Tip>
        <Step number="4">
          The post button changes to <UILabel>Post to Invoice</UILabel>. Confirm the <UILabel>Link Date</UILabel> and description, then click it.
        </Step>
        <Step number="5">
          The email is saved to SharePoint (the same way as <UILabel>Post to Job</UILabel>) and a link entry is written to that invoice's Status Log. It then shows up under the invoice on the website's Invoices tab.
        </Step>
        <Tip>
          Posting to an invoice records a Status Log link only &mdash; it does not create a Navision Record Link. Use <UILabel>Post to Job / Opportunity</UILabel> for record links.
        </Tip>
      </SectionCard>

      {/* 9. Effective dates & debug */}
      <SectionCard id="effective" title="9. Effective Dates & Debug Mode" isOpen={openSections.has('effective')} onToggle={() => toggle('effective')}>
        <h4 style={{ ...h4, marginTop: '12px' }}>Effective dates</h4>
        <p style={para}>
          When you post a status or revision (on the website or the toolbar) with an effective date of <strong>today</strong>, the current time is included, so same-day entries sort in the correct order. When you pick a <strong>past</strong> date, the time is ignored.
        </p>
        <h4 style={h4}>Debug Mode</h4>
        <p style={para}>
          If something doesn't post as expected, turn on <UILabel>Debug Mode</UILabel> (<UILabel>Settings</UILabel> &rarr; <UILabel>My Settings</UILabel>). Receivables then shows a <UILabel>Navision API calls</UILabel> panel listing each read and write &mdash; the request sent, the HTTP status, and Navision's response &mdash; which makes it clear exactly where a post or document lookup failed.
        </p>
      </SectionCard>

    </div>
  );
}
